import { useState } from 'react'

import { api } from '../../api/client'
import type { MedCespiteContratti, MedContratto } from '../../api/types'
import { useAuth } from '../../auth/AuthContext'
import { livelloModulo } from '../../auth/permessi'
import { useAsync } from '../../hooks/useAsync'
import { t } from '../../i18n/lingua'
import { Card, ErrorBanner, Spinner, formatDay } from '../common'
import { etichettaStatoContratto, etichettaTipoContratto } from '../contratti'
import { DataTable } from '../DataTable'
import { Finestra } from '../ModificheGitlab'
import { etichettaStatoCespite } from './comuni'

/**
 * CENSO MED (D5): the fixed asset and the contracts of a device, from its page.
 * The clinical engineering role does not open «Asset» or «Contratti»: here it
 * creates the device's asset (from a draft it sees first), links the device
 * to the contracts it may see and creates a maintenance contract.
 */

const CAMPI_CESPITE: { k: string; label: () => string; tipo?: string; span?: string }[] = [
  { k: 'asset_tag', label: () => t('Numero di cespite') },
  { k: 'purchase_date', label: () => t('Data di acquisto'), tipo: 'date' },
  { k: 'purchase_price', label: () => t('Prezzo di acquisto'), tipo: 'number' },
  { k: 'supplier', label: () => t('Fornitore') },
  { k: 'purchase_order', label: () => t('Ordine di acquisto') },
  { k: 'warranty_until', label: () => t('Garanzia fino al'), tipo: 'date' },
  { k: 'end_of_life', label: () => t('Fine vita'), tipo: 'date' },
  { k: 'notes', label: () => t('Note'), span: 'span-6' },
]

function FinestraCespite({ deviceId, bozza, onChiudi, onFatto }: {
  deviceId: string
  bozza: Record<string, unknown>
  onChiudi: () => void
  onFatto: (x: MedCespiteContratti) => void
}) {
  const [v, setV] = useState<Record<string, string>>(() =>
    Object.fromEntries(CAMPI_CESPITE.map((c) => [c.k, bozza[c.k] == null ? '' : String(bozza[c.k])])))
  const [revisione, setRevisione] = useState(false)
  const [errore, setErrore] = useState<unknown>(null)
  const [lavoro, setLavoro] = useState(false)

  async function crea() {
    setErrore(null)
    setLavoro(true)
    try {
      const campi = Object.fromEntries(Object.entries(v).filter(([, x]) => x.trim() !== '')
        .map(([k, x]) => [k, k === 'purchase_price' ? Number(x) : x.trim()]))
      onFatto(await api.medDeviceAssetCreate(deviceId, campi))
    } catch (e) {
      setErrore(e)
      setRevisione(false)
    } finally {
      setLavoro(false)
    }
  }

  const daCatalogo: [string, unknown][] = [
    [t('Nome'), bozza.name], [t('Numero di serie'), bozza.serial], [t('Produttore'), bozza.vendor],
    [t('Modello'), bozza.model], [t('Stato'), etichettaStatoCespite(String(bozza.status ?? ''))],
  ]
  return (
    <Finestra titolo={t('Crea il cespite')} onChiudi={onChiudi}>
      <ErrorBanner error={errore} />
      <div className="form-dialog">
        <h4 className="form__sezione">{t('Dalla scheda e dalla rete')}</h4>
        <dl className="kv">
          {daCatalogo.map(([k, x]) => (<div key={k} style={{ display: 'contents' }}><dt>{k}</dt><dd>{x == null || x === '' ? '—' : String(x)}</dd></div>))}
        </dl>
        <p className="field__hint">{t('Reparto, sede e centro di costo vengono dalla scheda.')}</p>
        {revisione ? (
          <>
            <h4 className="form__sezione">{t('Cosa scrivi tu')}</h4>
            <dl className="kv">
              {CAMPI_CESPITE.filter((c) => v[c.k].trim()).map((c) => (
                <div key={c.k} style={{ display: 'contents' }}><dt>{c.label()}</dt><dd>{v[c.k]}</dd></div>
              ))}
            </dl>
            <p className="field__hint">{t('Il cespite entra nel registro degli asset legato a questo dispositivo: uno solo per dispositivo.')}</p>
            <div className="form-dialog__azioni">
              <button type="button" className="secondary" onClick={() => setRevisione(false)} disabled={lavoro}>{t('Indietro')}</button>
              <button type="button" onClick={() => void crea()} disabled={lavoro}>{lavoro ? t('Creo…') : t('Crea il cespite')}</button>
            </div>
          </>
        ) : (
          <>
            <h4 className="form__sezione">{t('Cosa scrivi tu')}</h4>
            <div className="form-grid">
              {CAMPI_CESPITE.map((c) => (
                <label key={c.k} className={`field${c.span ? ` ${c.span}` : ''}`}>
                  <span className="field__label">{c.label()}</span>
                  <input type={c.tipo ?? 'text'} value={v[c.k]} onChange={(e) => setV({ ...v, [c.k]: e.target.value })}
                         min={c.tipo === 'number' ? 0 : undefined} />
                </label>
              ))}
            </div>
            <div className="form-dialog__azioni">
              <button type="button" className="secondary" onClick={onChiudi}>{t('Annulla')}</button>
              <button type="button" onClick={() => setRevisione(true)}>{t('Rivedi')}</button>
            </div>
          </>
        )}
      </div>
    </Finestra>
  )
}

function FinestraContratto({ deviceId, onChiudi, onFatto }: {
  deviceId: string | null
  onChiudi: () => void
  onFatto: () => void
}) {
  const [v, setV] = useState({ reference: '', name: '', vendor: '', contract_type: 'maintenance', start_date: '',
    end_date: '', value: '', service_level: '', notes: '' })
  const [collega, setCollega] = useState(Boolean(deviceId))
  const [errore, setErrore] = useState<unknown>(null)
  const [lavoro, setLavoro] = useState(false)
  const set = (k: keyof typeof v) => (e: { target: { value: string } }) => setV({ ...v, [k]: e.target.value })

  async function crea() {
    setErrore(null)
    setLavoro(true)
    try {
      await api.medContractCreate({
        reference: v.reference.trim(), name: v.name.trim() || null, vendor: v.vendor.trim(),
        contract_type: v.contract_type, start_date: v.start_date || null, end_date: v.end_date || null,
        value: v.value ? Number(v.value) : null, service_level: v.service_level.trim() || null,
        notes: v.notes.trim() || null, device_id: collega ? deviceId : null,
      })
      onFatto()
    } catch (e) {
      setErrore(e)
    } finally {
      setLavoro(false)
    }
  }

  return (
    <Finestra titolo={t('Nuovo contratto di manutenzione')} onChiudi={onChiudi}>
      <ErrorBanner error={errore} />
      <div className="form-dialog">
        <div className="form-grid">
          <label className="field"><span className="field__label">{t('Riferimento')}</span>
            <input type="text" value={v.reference} onChange={set('reference')} maxLength={120} required /></label>
          <label className="field"><span className="field__label">{t('Fornitore')}</span>
            <input type="text" value={v.vendor} onChange={set('vendor')} maxLength={200} required /></label>
          <label className="field"><span className="field__label">{t('Tipo')}</span>
            <select value={v.contract_type} onChange={set('contract_type')}>
              {['maintenance', 'support', 'warranty', 'lease'].map((k) => <option key={k} value={k}>{etichettaTipoContratto(k)}</option>)}
            </select></label>
          <label className="field span-4"><span className="field__label">{t('Nome')}</span>
            <input type="text" value={v.name} onChange={set('name')} maxLength={200} /></label>
          <label className="field"><span className="field__label">{t('Inizio')}</span>
            <input type="date" value={v.start_date} onChange={set('start_date')} /></label>
          <label className="field"><span className="field__label">{t('Scadenza')}</span>
            <input type="date" value={v.end_date} onChange={set('end_date')} /></label>
          <label className="field"><span className="field__label">{t('Valore')}</span>
            <input type="number" min={0} value={v.value} onChange={set('value')} /></label>
          <label className="field span-6"><span className="field__label">{t('Livello di servizio')}</span>
            <input type="text" value={v.service_level} onChange={set('service_level')} maxLength={200}
                   placeholder={t('per esempio: intervento entro 8 ore, 2 manutenzioni preventive l’anno')} /></label>
          <label className="field span-6"><span className="field__label">{t('Note')}</span>
            <textarea rows={2} value={v.notes} onChange={set('notes')} maxLength={4000} /></label>
          {deviceId && (
            <div className="checkbox span-6">
              <input id="med-contratto-collega" type="checkbox" checked={collega} onChange={(e) => setCollega(e.target.checked)} />
              <label htmlFor="med-contratto-collega">{t('Copri subito questo dispositivo')}</label>
            </div>
          )}
        </div>
        <p className="field__hint">{t('Il contratto entra nel registro dei contratti, visibile all’Ingegneria clinica.')}</p>
        <div className="form-dialog__azioni">
          <button type="button" className="secondary" onClick={onChiudi} disabled={lavoro}>{t('Annulla')}</button>
          <button type="button" onClick={() => void crea()} disabled={lavoro || !v.reference.trim() || !v.vendor.trim()}>{t('Crea il contratto')}</button>
        </div>
      </div>
    </Finestra>
  )
}

export function CespiteContratti({ deviceId }: { deviceId: string }) {
  const { principal } = useAuth()
  const scrive = livelloModulo(principal, 'medical') === 'write'
  const dati = useAsync(() => api.medDeviceAsset(deviceId), [deviceId])
  const disponibili = useAsync(() => api.medContracts(), [deviceId])
  const [finestra, setFinestra] = useState<'cespite' | 'contratto' | null>(null)
  const [scelto, setScelto] = useState('')
  const [errore, setErrore] = useState<unknown>(null)
  const x = dati.data

  async function esegui(azione: () => Promise<unknown>) {
    setErrore(null)
    try {
      await azione()
      await dati.reload()
      await disponibili.reload()
    } catch (e) {
      setErrore(e)
    }
  }

  if (dati.loading && !x) return <Spinner />
  if (!x) return <ErrorBanner error={dati.error} />
  const collegati = new Set(x.contracts.map((c) => c.id))
  const scelte = (disponibili.data?.contracts ?? []).filter((c) => !collegati.has(c.id))

  return (
    <>
      <ErrorBanner error={errore} />
      <Card title={t('Cespite')}>
        {x.asset ? (
          <dl className="kv">
            <dt>{t('Numero di cespite')}</dt><dd className="mono">{x.asset.asset_tag}</dd>
            <dt>{t('Stato')}</dt><dd>{etichettaStatoCespite(x.asset.status)}</dd>
            <dt>{t('Data di acquisto')}</dt><dd>{formatDay(x.asset.purchase_date)}</dd>
            <dt>{t('Prezzo di acquisto')}</dt><dd>{x.asset.purchase_price == null ? '—' : `${x.asset.purchase_price.toLocaleString()} ${x.asset.currency ?? ''}`}</dd>
            <dt>{t('Fornitore')}</dt><dd>{x.asset.supplier || '—'}</dd>
            <dt>{t('Garanzia fino al')}</dt><dd>{formatDay(x.asset.warranty_until)}</dd>
            <dt>{t('Fine vita')}</dt><dd>{formatDay(x.asset.end_of_life)}</dd>
          </dl>
        ) : (
          <>
            <p className="field__hint" style={{ marginTop: 0 }}>
              {x.draft_error ?? t('Il dispositivo non è ancora nel registro dei beni. Il cespite si crea dalla scheda: nome, seriale, produttore, modello, reparto e sede sono già qui.')}
            </p>
            {scrive && x.draft && <button type="button" onClick={() => setFinestra('cespite')}>{t('Crea il cespite…')}</button>}
          </>
        )}
      </Card>
      <Card title={t('Contratti')}>
        <DataTable
          rows={x.contracts}
          rowKey={(c) => c.id}
          paginazione={false}
          strumenti={false}
          emptyText={t('Nessun contratto copre questo dispositivo.')}
          columns={[
            { key: 'reference', header: t('Riferimento'), mono: true, value: (c) => c.reference },
            { key: 'name', header: t('Nome'), value: (c) => c.name ?? '' },
            { key: 'vendor', header: t('Fornitore'), value: (c) => c.vendor },
            { key: 'type', header: t('Tipo'), value: (c) => etichettaTipoContratto(c.contract_type) },
            { key: 'status', header: t('Stato'), value: (c) => etichettaStatoContratto(c.effective_status) },
            { key: 'end_date', header: t('Scadenza'), type: 'date', value: (c) => c.end_date ?? '', render: (c) => formatDay(c.end_date) },
            { key: 'service_level', header: t('Livello di servizio'), value: (c) => c.service_level ?? '', hidden: true },
            ...(scrive ? [{
              key: 'azione', header: '', sort: false, searchable: false,
              render: (c: MedContratto) => (
                <button type="button" className="secondary"
                        onClick={() => { if (window.confirm(t('Togliere questo dispositivo dalla copertura di «{r}»?', { r: c.reference }))) void esegui(() => api.medDeviceContractUnlink(deviceId, c.id)) }}>
                  {t('Scollega')}
                </button>
              ),
            }] : []),
          ]}
        />
        {scrive && x.asset && (
          <div className="med-opzioni" style={{ marginTop: 12 }}>
            <label className="field" style={{ margin: 0, minWidth: 260 }}>
              <span className="field__label">{t('Collega a un contratto')}</span>
              <select value={scelto} onChange={(e) => setScelto(e.target.value)}>
                <option value="">{scelte.length ? t('scegli…') : t('nessun contratto visibile all’Ingegneria clinica')}</option>
                {scelte.map((c) => (
                  <option key={c.id} value={c.id}>{c.reference}{c.name ? ` · ${c.name}` : ''} · {c.vendor}{c.end_date ? ` · ${formatDay(c.end_date)}` : ''}</option>
                ))}
              </select>
            </label>
            <button type="button" className="secondary" disabled={!scelto}
                    onClick={() => void esegui(async () => { await api.medDeviceContractLink(deviceId, scelto); setScelto('') })}>{t('Collega')}</button>
            <button type="button" className="secondary" onClick={() => setFinestra('contratto')}>{t('Nuovo contratto di manutenzione…')}</button>
          </div>
        )}
        {scrive && !x.asset && <p className="field__hint">{t('Per collegare un contratto serve il cespite: il contratto copre il bene.')}</p>}
      </Card>
      {finestra === 'cespite' && x.draft && (
        <FinestraCespite deviceId={deviceId} bozza={x.draft} onChiudi={() => setFinestra(null)}
                         onFatto={() => { setFinestra(null); void dati.reload() }} />
      )}
      {finestra === 'contratto' && (
        <FinestraContratto deviceId={x.asset ? deviceId : null} onChiudi={() => setFinestra(null)}
                           onFatto={() => { setFinestra(null); void dati.reload(); void disponibili.reload() }} />
      )}
    </>
  )
}
